import React, { useEffect, useRef, useState } from "react";
import "./JobPage.scss";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { JobInfo } from "../../../../../components/job/Job";
import { selectUser } from "../../../../../core/auth/authSlice";
import { addMessage } from "../../../../../core/auth/chatSlice";
import { User } from "../../../../../shared/models";
import http from "../../../../../shared/utils/http";

import { isToday, isYesterday } from "date-fns";
import { useTranslation } from "react-i18next";
import Button from "../../../../../components/essentials/button/Button";
import ClickIcon from "../../../../../components/essentials/icon-buttons/click-icon/ClickIcon";
import { getDescriptionHtml } from "../../../../../components/essentials/input/text-area/RichTextInput";
import AcedModal from "../../../../../components/essentials/modals/aced-modal/AcedModal";
import ConfirmationModal from "../../../../../components/essentials/modals/confirmation-modal/ConfirmationModal";
import UserCard from "../../../../../components/essentials/user-card/UserCard";
import { JobContentBody } from "../../../../../components/job/content/JobContent";
import { selectPosition } from "../../../../../components/job/utils/SelectPosition";
import { SendButton } from "../../../../../components/post/components/SendBtn/SendButton";
import { JobApiRoutes } from "../../../../../shared/api-routes/job/job-api.routes";
import { capitalizeName } from "../../../../../shared/utils/util";

const JobPage: React.FC = () => {
	const [isConfModalOpen, setConfModalOpen] = useState(false);
	const [isConsentModalOpen, setConsentModalOpen] = useState(false);
	const [jobInfo, setJobInfo] = useState<JobInfo>();
	const [applicants, setApplicants] = useState<User[]>([]);
	const params = useParams();
	const { actorId } = useSelector(selectUser) as User;

	const scrollRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		scrollRef.current?.scrollTo(0, 0);
		http.get(JobApiRoutes.GET_JOB_BY_ID(params.jobId)).then((res) => {
			setJobInfo(res);
			// If current user is the job author, fetch applicants
			if (res.author.id === actorId) {
				http
					.get(JobApiRoutes.GET_JOB_APPLICANTS(params.jobId))
					.then((applicants) => {
						setApplicants(applicants);
					})
					.catch((err) => {
						console.log("Error fetching applicants:", err);
					});
			}
		});
	}, [params.jobId, scrollRef.current, actorId]);

	const { t } = useTranslation();

	const formatTime = (dateString: string) => {
		const date = new Date(dateString);

		if (isToday(date)) {
			const hour = date.getUTCHours();
			const minute = date.getUTCMinutes();
			return `${hour}:${minute}`;
		} else if (isYesterday(date)) {
			return "Yesterday";
		} else {
			const day = date.getDate();
			const month = date.getMonth();
			const year = date.getFullYear();
			return ` ${t("common.months.long")[month]} ${day} ${year}`;
		}
	};

	const sendApplication = () => {
		http
			.post(JobApiRoutes.SEND_APPLICATION(jobInfo?.id))
			.then((res) => {
				setConsentModalOpen(false);
				setConfModalOpen(true);
			})
			.catch((err) => {
				console.log(err);
			});
	};
	const navigate = useNavigate();
	const dispatch = useDispatch();

	if (!jobInfo) return <></>;

	const selectPositionHelper = (): string => {
		if (!jobInfo.author.accountType) return "";

		return selectPosition(
			jobInfo.position,
			jobInfo.subject,
			jobInfo.sport,
			jobInfo.author.accountType,
			t
		);
	};

	return (
		<div
			ref={scrollRef}
			className="job_page_container"
		>
			<div className="job-page">
				<div className="job-page-header">
					<ClickIcon
						// tooltip="Close job"
						icon="close"
						onClick={() => {
							navigate("/jobs");
						}}
					/>
				</div>
				<div className="title-bar">
					{jobInfo && (
						<UserCard
							userInfo={jobInfo?.author}
							action="follow"
						/>
					)}
				</div>

				<div className="content">
					<h1>{selectPositionHelper()}</h1>
					<p
						className="post-content__content"
						dangerouslySetInnerHTML={{
							__html: getDescriptionHtml(jobInfo?.description),
						}}
					></p>

					<div className="divider" />

					<div className="job__content">
						<JobContentBody jobInfo={jobInfo} />
					</div>
				</div>

				{/* Show applicants list if current user is the job author */}
				{actorId === jobInfo?.author.id && applicants.length > 0 && (
					<div className="job__applicants">
						<h3>Applicants ({applicants.length})</h3>
						<div className="applicants-list">
							{applicants.map((applicant) => (
								<UserCard
									key={applicant.id}
									userInfo={applicant}
									action="follow"
								/>
							))}
						</div>
					</div>
				)}

				{/* Only show apply button if current user is not the job author */}
				{actorId !== jobInfo?.author.id && (
					<div className="job__apply">
						<div
							className="app-button"
							onClick={() => {
								setConsentModalOpen(true);
							}}
						>
							<p>{jobInfo?.subject === "team" ? "Apply" : "Contact"}</p>
						</div>
					</div>
				)}

				<div className="job__footer">
					<div className="group">
						<SendButton
							title="Send Job"
							disabled={true}
							onSend={async (chatIds: string[]) => {
								const messages = await http.post(
									JobApiRoutes.SHARE_JOB(jobInfo.id),
									{
										chatIds,
									}
								);
								chatIds.forEach((chatId, index) => {
									dispatch(
										addMessage({
											chatId,
											message: messages[index],
										})
									);
								});
							}}
						/>
						<ClickIcon
							icon="share"
							tooltip="Share"
							onClick={() => {
								if (navigator.share) {
									navigator.share({
										title: selectPositionHelper(),
										url: window.location.href,
									});
								} else {
									navigator.clipboard.writeText(window.location.href);
								}
							}}
						/>
					</div>
				</div>
			</div>

			{isConsentModalOpen && (
				<ConfirmationModal
					title={`Approve sharing your information with ${capitalizeName(
						jobInfo?.author.name
					)} ${capitalizeName(jobInfo?.author.surname)}?`}
					state={{
						isModalOpen: isConsentModalOpen,
						setModalOpen: setConsentModalOpen,
					}}
					onConfirm={sendApplication}
				/>
			)}

			{isConfModalOpen && (
				<AcedModal
					hideClose
					state={{
						isModalOpen: isConfModalOpen,
						setModalOpen: setConfModalOpen,
					}}
					title="Your application has been sent"
					className="confirmation-modal"
				>
					<div className="delete-modal">
						<div className="options">
							<Button
								className="btn btn-primary"
								action={() => {
									setConfModalOpen(false);
								}}
							>
								OK
							</Button>
						</div>
					</div>
				</AcedModal>
			)}

			{/* {showModalCompatibility &&
        createPortal(
          <ModalConfirm
            text="Your application has been sent"
            onClose={() => setShowModal(false)}
          />,
          document.body
        )} */}
		</div>
	);
};

export default JobPage;

/* <div className="stats">
          <div className="d-flex column justify-space-between">
            <p className="stats__title">Requested Skills </p>
            
            <div className="stats__divider"></div>

            <ProgressBar
              title={"Height"}
              value=">175"
              className="white"
              ></ProgressBar>

            <ProgressBar
              title={"Reach"}
              value=">225cm"
              className="white"
              ></ProgressBar>
              
            <ProgressBar
              title={"Aces per match"}
              value=">2"
              className="white"
              ></ProgressBar>
              
            <ProgressBar
              title={"Years of experience"}
              value=">5"
              className="white"
            ></ProgressBar>
          </div>
        </div> */

// <View style={styles.container}>
//   <ScrollView showsVerticalScrollIndicator={false} overScrollMode={"never"}>
//     {/* <Modal //compatibility
//       isVisible={isCompVisible}
//       animationInTiming={500}
//       onBackdropPress={() => dispatch(setCompVisible(false))}
//       onModalHide={() => {
//         dispatch(setCompVisible(!isCompVisible));
//       }}
//       children={<Compatibility adIcon={postInfo.pic} />}
//     />
//     <Modal
//       isVisible={isAppliedVisible}
//       onBackdropPress={() => {
//         dispatch(setAppliedVisible(false));
//         dispatch(setPInterno(false));
//       }}
//       onModalHide={() => {
//         dispatch(setAppliedVisible(!isAppliedVisible));
//       }}
//       children={<ConfirmApply />}
//     ></Modal> */}
//     <View style={styles.postContainer}>
//       <Post_Picture postInfo={postInfo} navigation={navigation} />
//       <Post_Title postInfo={postInfo} />
//       <Post_Info postInfo={postInfo} />
//       <Post_Content postInfo={postInfo} />
//       <Post_Stats />
//     </View>
//   </ScrollView>
// </View>
